import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { getSiteSettings } from "../api/siteSettings";

const AppSettingsContext = createContext(null);
AppSettingsContext.displayName = "AppSettingsContext";

export const AppSettingsProvider = ({ children }) => {
  const [globalBannerMessage, setGlobalBannerMessage] = useState(null);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [minAppVersion, setMinAppVersion] = useState(null);
  const [isChecked, setIsChecked] = useState(false);
  const inFlightRef = useRef(false);

  const checkAppSettings = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      const data = await getSiteSettings();
      setGlobalBannerMessage(data.globalBannerMessage || null);
      setMaintenanceMode(Boolean(data.maintenanceMode));
      setMinAppVersion(data.minAppVersion ?? null);
    } catch (error) {
      console.error("App settings check failed:", error);
    } finally {
      setIsChecked(true);
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    checkAppSettings();
  }, [checkAppSettings]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        checkAppSettings();
      }
    };

    const handleFocus = () => {
      checkAppSettings();
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleFocus);
    };
  }, [checkAppSettings]);

  const value = {
    globalBannerMessage,
    maintenanceMode,
    minAppVersion,
    isChecked,
    checkAppSettings,
  };

  return (
    <AppSettingsContext.Provider value={value}>
      {children}
    </AppSettingsContext.Provider>
  );
};

export const useAppSettings = () => {
  const context = useContext(AppSettingsContext);
  if (!context) {
    throw new Error("useAppSettings must be used within an AppSettingsProvider");
  }
  return context;
};

export default AppSettingsContext;
